import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Grid,
  Divider,
  alpha,
  IconButton,
  MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../supabaseClient';

const MarkPaymentDialog = ({ open, onClose, payable, onSuccess, companyId, canEdit = true }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentType, setPaymentType] = useState('full'); // 'full' or 'partial'
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && payable) {
      setPaymentType('full');
      setPaymentAmount(payable.remaining_amount?.toFixed(2) || '0.00');
      setPaymentDate(dayjs());
      setPaymentMethod('check');
      setPaymentReference('');
      setNotes('');
      setError(null);
    }
  }, [open, payable]);

  const handlePaymentTypeChange = (event) => {
    const type = event.target.value;
    setPaymentType(type);
    if (type === 'full') {
      setPaymentAmount(payable.remaining_amount?.toFixed(2) || '0.00');
    } else {
      setPaymentAmount('');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const validatePayment = () => {
    const amount = parseFloat(paymentAmount);
    
    // Check if amount is a valid number
    if (isNaN(amount)) {
      setError('Please enter a valid payment amount');
      return false;
    }
    
    // Check if amount is positive
    if (amount <= 0) {
      setError('Payment amount must be greater than 0');
      return false;
    }
    
    // Check if amount exceeds remaining balance
    if (amount > payable.remaining_amount) {
      setError(`Payment amount cannot exceed the remaining balance of ${formatCurrency(payable.remaining_amount)}`);
      return false;
    }
    
    // Check if payment date is provided
    if (!paymentDate) {
      setError('Please select a payment date');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      setError('You do not have permission to mark payments.');
      return;
    }

    if (!validatePayment()) return;

    setLoading(true);
    setError(null);

    try {
      const amount = parseFloat(paymentAmount);
      const newPaidAmount = (payable.paid_amount || 0) + amount;
      const newRemainingAmount = payable.total_amount - newPaidAmount;
      
      let newPaymentStatus = 'unpaid';
      if (newRemainingAmount <= 0.01) { // Account for floating point precision
        newPaymentStatus = 'paid';
      } else if (newPaidAmount > 0) {
        newPaymentStatus = 'partial';
      }

      // Determine the correct transaction table (handle special cases)
      let transactionTable;
      if (payable.transaction_type === 'bottles') {
        transactionTable = 'bottle_transactions'; // bottles uses singular 'bottle'
      } else if (payable.transaction_type === 'labels') {
        transactionTable = 'label_transactions'; // labels uses singular 'label'
      } else if (payable.transaction_type === 'supplies') {
        transactionTable = 'supply_transactions'; // supplies uses singular 'supply'
      } else if (payable.transaction_type === 'expense') {
        transactionTable = 'expenses'; // expenses table, not expense_transactions
      } else if (payable.transaction_type === 'products') {
        transactionTable = 'product_inventory_transactions'; // products use full table name
      } else {
        transactionTable = `${payable.transaction_type}_transactions`;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Create a JSON object to store payment details in notes
      const paymentHistoryEntry = {
        amount: amount,
        date: paymentDate.format('YYYY-MM-DD'),
        method: paymentMethod,
        reference: paymentReference || null,
        notes: notes || null,
        timestamp: new Date().toISOString(),
        user_id: user?.id
      };

      // Get existing payment history from notes field
      let paymentHistory = [];
      if (payable.notes) {
        try {
          // Try to parse existing notes as JSON array
          paymentHistory = JSON.parse(payable.notes);
          if (!Array.isArray(paymentHistory)) {
            // If it's not an array, create new array with old notes as string
            paymentHistory = [{ legacy_note: payable.notes }];
          }
        } catch {
          // If parse fails, it's a regular string note
          paymentHistory = [{ legacy_note: payable.notes }];
        }
      }
      
      // Add new payment to history
      paymentHistory.push(paymentHistoryEntry);

      // Update the transaction with payment status and history
      // Note: Expenses use 'payment_notes' instead of 'notes' for payment history
      const updateData = {
        payment_status: newPaymentStatus,
        paid_amount: newPaidAmount
      };
      
      // Add payment history to the appropriate field
      if (payable.transaction_type === 'expense') {
        updateData.payment_notes = JSON.stringify(paymentHistory);
      } else {
        updateData.notes = JSON.stringify(paymentHistory);
      }
      
      const { error: updateError } = await supabase
        .from(transactionTable)
        .update(updateData)
        .eq('id', payable.transaction_id);

      if (updateError) throw updateError;

      // Show success and close
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error updating payment:', err);
      setError('Failed to update payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!payable) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PaymentIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={400}>
                Mark Payment
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {payable.invoice_number || 'Invoice'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Payment Summary */}
          <Box sx={{ 
            p: 2, 
            mb: 3, 
            mt: 3,
            background: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h6" fontWeight={400}>
                  {formatCurrency(payable.total_amount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Already Paid
                </Typography>
                <Typography variant="h6" fontWeight={400} color="success.main">
                  {formatCurrency(payable.paid_amount)}
                </Typography>
                {payable.paid_amount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {((payable.paid_amount / payable.total_amount) * 100).toFixed(1)}% paid
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Remaining Balance
                </Typography>
                <Typography variant="h5" fontWeight={400} color="error.main">
                  {formatCurrency(payable.remaining_amount)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  This is the maximum amount you can pay
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Payment Type */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={400} gutterBottom>
              Payment Type
            </Typography>
            <RadioGroup
              value={paymentType}
              onChange={handlePaymentTypeChange}
            >
              <FormControlLabel
                value="full"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={400}>
                      Full Payment
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pay the full remaining balance: {formatCurrency(payable.remaining_amount)}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="partial"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={400}>
                      Partial Payment
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pay a portion of the remaining balance
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>

          {/* Payment Amount */}
          <TextField
            label="Payment Amount"
            type="number"
            fullWidth
            value={paymentAmount}
            onChange={(e) => {
              const value = e.target.value;
              // Prevent negative values
              if (value === '' || parseFloat(value) >= 0) {
                setPaymentAmount(value);
                // Clear error when user types a valid amount
                if (error && parseFloat(value) > 0 && parseFloat(value) <= payable.remaining_amount) {
                  setError(null);
                }
              }
            }}
            onBlur={(e) => {
              const value = parseFloat(e.target.value);
              // If value exceeds remaining balance, cap it
              if (value > payable.remaining_amount) {
                setPaymentAmount(payable.remaining_amount.toFixed(2));
                setError(`Amount capped at remaining balance: ${formatCurrency(payable.remaining_amount)}`);
              }
            }}
            disabled={paymentType === 'full'}
            inputProps={{
              step: '0.01',
              min: '0.01',
              max: payable.remaining_amount
            }}
            sx={{ mb: 3 }}
            helperText={
              paymentType === 'partial' 
                ? `Maximum: ${formatCurrency(payable.remaining_amount)} (Remaining balance)` 
                : 'Full payment amount'
            }
          />

          {/* Payment Date */}
          <DatePicker
            label="Payment Date"
            value={paymentDate}
            onChange={(newValue) => setPaymentDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: { mb: 3 }
              }
            }}
          />

          {/* Payment Method */}
          <TextField
            select
            label="Payment Method"
            fullWidth
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            sx={{ mb: 3 }}
          >
            <MenuItem value="check">Check</MenuItem>
            <MenuItem value="wire">Wire Transfer</MenuItem>
            <MenuItem value="ach">ACH</MenuItem>
            <MenuItem value="credit_card">Credit Card</MenuItem>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          {/* Payment Reference */}
          <TextField
            label="Payment Reference"
            fullWidth
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.target.value)}
            placeholder="Check number, transaction ID, etc."
            sx={{ mb: 3 }}
            helperText="Optional reference number or identifier"
          />

          {/* Notes */}
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional payment notes..."
            helperText="Optional notes about this payment"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={loading || !canEdit}
            startIcon={<PaymentIcon />}
          >
            {loading ? 'Processing...' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default MarkPaymentDialog;
